import { request } from './client'

/* Linen and consumables, as the server sends them.

   NOTE THE THREE NUMBERS on the on-hand view. The first version of this had one, called
   `totalOwned`, and it lied: it summed the stock levels — stock in places we can count —
   and issuing four towels to a room made it fall by four. A circulating towel in a room
   is still the hotel's towel. The three numbers keep that straight, and the third is the
   one no other system has. */

export type StockItemKind = 'CIRCULATING' | 'CONSUMABLE'

/* **#172 — `TROLLEY` WAS MISSING FROM THIS UNION SINCE SPRINT 44.** The server has sent
   it since M-09 gave every pair a trolley: `Wire.Of(StockLocationKind)` has had the word
   for twenty-five sprints and this line never learned it.

   Nothing failed, which is how it survived: a union that is too NARROW does not stop a
   value arriving, it stops a screen from being able to handle it. Any code switching on
   `locationKind` had a branch TypeScript said was unreachable, and the compiler agreed —
   so the honest branch was the one that could not be written. W-109 is the first screen
   that lists trolleys, and it is what found this. */
export type StockLocationKind = 'STORE' | 'PANTRY' | 'LAUNDRY' | 'TROLLEY'

/* SCREAMING_SNAKE like every other enum on this wire. It was PascalCase until Sprint 16
   because the endpoint parsed it with Enum.Parse(ignoreCase: true) — which also read a
   bare number as a member, so "5" would have written stock off as a Loss. The server now
   accepts exactly these six spellings and refuses everything else. */
export type StockMovementKind =
  | 'RECEIPT' | 'TRANSFER' | 'ISSUE' | 'RETURN' | 'LOSS' | 'ADJUSTMENT'

export interface StockLevelView {
  stockItemId: string
  itemCode: string
  itemName: string
  itemNameAr: string | null
  kind: StockItemKind
  unit: string
  stockLocationId: string
  locationName: string
  locationNameAr: string | null
  /** STORE, PANTRY or LAUNDRY. Sent because it changes what a screen may OFFER — a
   *  delivery arrives at the store, so a handset standing in a pantry must not offer
   *  "received a delivery". Matching on the word "store" in a name is not a rule. */
  locationKind: StockLocationKind
  quantity: number
  /** **What this place should hold, or null because nobody has said** (W-104, Sprint 67).
   *  It was one number per item applied to the store, every pantry, the laundry and every
   *  trolley alike — so the store never read as short and a trolley was permanently amber. */
  parLevel: number | null
  /** **Null is a third answer** and a screen must draw it differently from "no". */
  isBelowPar: boolean | null
}

export interface StockOnHandView {
  stockItemId: string
  itemCode: string
  itemName: string
  itemNameAr: string | null
  kind: StockItemKind
  unit: string
  /** From the LEDGER: received, less written off, less consumables issued. */
  totalOwned: number
  /** In a place we can count — store, pantries, laundry. */
  inLocations: number
  /** Owned minus in-locations: linen out on rails. Zero for consumables. */
  inRooms: number
  /** The sum of every place's par, or null where none is set. See `StockLevelView`. */
  parLevel: number | null
}

/* ── W-108 REPLENISHMENT AND W-104 PAR LEVELS (Sprint 67) ──────────────────────

   W-100 answers "what is where"; this answers "what do I put on the trolley", which is a
   subtraction per item per floor plus one fact a balances table cannot show at all —
   whether the store can cover it. */

export interface ReplenishmentRow {
  stockItemId: string
  itemCode: string
  itemName: string
  itemNameAr: string | null
  unit: string
  inPantry: number
  parLevel: number
  shortfall: number
  inStore: number
  /** The shortfall, clamped to what the store actually holds. */
  suggested: number
}

export interface ReplenishmentPantry {
  stockLocationId: string
  name: string
  nameAr: string | null
  floorCode: string | null
  rows: ReplenishmentRow[]
}

/** An item the store cannot cover even if it sends everything. A purchase order. */
export interface ReplenishmentShortage {
  stockItemId: string
  itemCode: string
  itemName: string
  itemNameAr: string | null
  unit: string
  neededAcrossPantries: number
  inStore: number
}

export interface ReplenishmentBoard {
  stockLocationId: string | null
  storeLocationId: string | null
  storeName: string | null
  storeNameAr: string | null
  pantries: ReplenishmentPantry[]
  shortOverall: ReplenishmentShortage[]
}

export interface StockParRow {
  stockLocationId: string
  locationName: string
  locationNameAr: string | null
  locationKind: StockLocationKind
  stockItemId: string
  itemCode: string
  itemName: string
  itemNameAr: string | null
  unit: string
  onHand: number
  /** Null where nobody has said what this place should hold. */
  parLevel: number | null
}

export const fetchReplenishment = (
  propertyId: string, options: { signal?: AbortSignal } = {},
) => request<ReplenishmentBoard>(
  `/properties/${propertyId}/replenishment`,
  options.signal ? { signal: options.signal } : {})

/** **The quantities are the person's, not the board's.** It proposed; he corrected. */
export const sendReplenishment = (
  propertyId: string,
  lines: { stockLocationId: string; stockItemId: string; quantity: number }[],
) => request<{ moved: number }>(
  `/properties/${propertyId}/replenishment`, { method: 'POST', body: { lines } })

/* ── W-103 · WHAT IS AT THE LAUNDRY (Sprint 68) ────────────────────────────────

   W-100 says the laundry holds sixty towels. Sixty that went down this morning and sixty
   that went down on Saturday are the same number and opposite situations. */

export interface LaundryRow {
  stockItemId: string
  itemCode: string
  itemName: string
  itemNameAr: string | null
  unit: string
  atLaundry: number
  /** When the oldest piece still there went down. **Null means nothing is there.** */
  oldestSentAtUtc: string | null
  hoursOut: number | null
  isOverdue: boolean
  /** The last week's rhythm: sending far more than is coming back is the early shape. */
  sentRecently: number
  returnedRecently: number
}

export interface LaundryBoard {
  laundryLocationId: string | null
  laundryName: string | null
  laundryNameAr: string | null
  /** The client's own answer, CQ-142 — sent so the screen and the rule cannot drift. */
  turnaroundHours: number
  overdue: number
  rows: LaundryRow[]
}

export const fetchLaundry = (
  propertyId: string, options: { signal?: AbortSignal } = {},
) => request<LaundryBoard>(
  `/properties/${propertyId}/laundry`,
  options.signal ? { signal: options.signal } : {})

/* ── W-109 · THE PLACES A HOTEL KEEPS ITS LINEN (Sprint 69) ────────────────────

   The gap W-104 exposed: every screen in this area was built on a set of places only the
   demonstration seed could create. */

export interface StockPlaceRow {
  stockLocationId: string
  code: string
  name: string
  nameAr: string | null
  kind: StockLocationKind
  floorId: string | null
  floorCode: string | null
  /** Set on a trolley. Read-only here — a trolley belongs to the pair, not to this screen. */
  teamId: string | null
  teamName: string | null
  isActive: boolean
  /** **Nought is the only number that makes it removable.** The ledger points at these. */
  movementsEver: number
  itemsHeld: number
  parsSet: number
}

export const fetchStockPlaces = (
  propertyId: string, options: { signal?: AbortSignal } = {},
) => request<StockPlaceRow[]>(
  `/properties/${propertyId}/stock-places`,
  options.signal ? { signal: options.signal } : {})

export const addStockPlace = (
  propertyId: string,
  body: { code: string; name: string; nameAr?: string | null; kind: StockLocationKind; floorId?: string | null },
) => request<StockPlaceRow[]>(
  `/properties/${propertyId}/stock-places`, { method: 'POST', body })

export const saveStockPlace = (
  propertyId: string, id: string,
  body: { name: string; nameAr?: string | null; floorId?: string | null },
) => request<StockPlaceRow[]>(
  `/properties/${propertyId}/stock-places/${id}`, { method: 'PUT', body })

export const setStockPlaceActive = (
  propertyId: string, id: string, isActive: boolean,
) => request<StockPlaceRow[]>(
  `/properties/${propertyId}/stock-places/${id}/active`, { method: 'PUT', body: { isActive } })

export const deleteStockPlace = (
  propertyId: string, id: string,
) => request<StockPlaceRow[]>(
  `/properties/${propertyId}/stock-places/${id}`, { method: 'DELETE' })

/* ── W-96 · THE THINGS A HOTEL COUNTS (Sprint 80) ──────────────────────────────

   The same gap on the other half of the ledger's vocabulary: a hotel could correct our
   Arabic for "bath towel" and could not say "we also issue slippers" at all. */

/** `piece`, `pair`, `set`. **A closed list**, because it is printed beside a number in
 *  both languages — a free text unit is an English word arriving as DATA in the middle of
 *  an Arabic line, which is the shape of #74, #82, #83 and #102. */
export type StockUnit = 'piece' | 'pair' | 'set'

export const STOCK_UNITS: readonly StockUnit[] = ['piece', 'pair', 'set']

export interface StockItemRow {
  stockItemId: string
  code: string
  name: string
  nameAr: string | null
  kind: StockItemKind
  unit: StockUnit
  /* **NO PRICE.** CQ-129 was answered on 20 September: the hotel keeps no cost in this
     system, only the count. The field, the variance screen's value figure and the money
     column on the consumables report went with it in Sprint 84. */
  isActive: boolean
  /** **Nought is the only number that makes it removable**, and the only one that leaves
   *  the kind still choosable. */
  movementsEver: number
  /** Everywhere added up, including what is out in rooms. */
  quantityOwned: number
  parsSet: number
  standardsSet: number
}

export const fetchStockItems = (
  propertyId: string, options: { signal?: AbortSignal } = {},
) => request<StockItemRow[]>(
  `/properties/${propertyId}/stock-items`,
  options.signal ? { signal: options.signal } : {})

export const addStockItem = (
  propertyId: string,
  body: {
    code: string; name: string; nameAr?: string | null
    kind: StockItemKind; unit: StockUnit
  },
) => request<StockItemRow[]>(
  `/properties/${propertyId}/stock-items`, { method: 'POST', body })

/** `kind` is OMITTED for an item the ledger has touched — absent means "leave it alone",
 *  and sending a different one is refused with `stock.item.kindSettled`. */
export const saveStockItem = (
  propertyId: string, id: string,
  body: {
    name: string; nameAr?: string | null
    kind?: StockItemKind; unit: StockUnit
  },
) => request<StockItemRow[]>(
  `/properties/${propertyId}/stock-items/${id}`, { method: 'PUT', body })

export const setStockItemActive = (
  propertyId: string, id: string, isActive: boolean,
) => request<StockItemRow[]>(
  `/properties/${propertyId}/stock-items/${id}/active`, { method: 'PUT', body: { isActive } })

export const deleteStockItem = (
  propertyId: string, id: string,
) => request<StockItemRow[]>(
  `/properties/${propertyId}/stock-items/${id}`, { method: 'DELETE' })

/* ── W-101 · HOW IT GOT TO FOUR (Sprint 80) ────────────────────────────────────

   Every balance in this product is derived from the movement ledger and nothing listed
   it, so the only answer to "why does it say four?" was "because it does". */

export interface LedgerRow {
  stockMovementId: string
  occurredAtUtc: string
  kind: StockMovementKind
  stockItemId: string
  itemCode: string
  /** **Positive except on an adjustment**, where the sign IS the correction. Direction is
   *  carried by from/to, not by a minus sign. */
  quantity: number
  unit: string
  fromLocationId: string | null
  fromCode: string | null
  toLocationId: string | null
  toCode: string | null
  roomNumber: string | null
  /** The sentence somebody typed on a loss or an adjustment. **Never translated.** */
  reason: string | null
  actorUserId: string
  actorName: string
  actorNameAr: string | null
  /** Set where the movement was part of a count — a shelf correction, not a Tuesday one. */
  stockCountId: string | null
}

export interface LedgerItem {
  stockItemId: string; code: string; name: string; nameAr: string | null
}

export interface LedgerPlace {
  stockLocationId: string; code: string; name: string; nameAr: string | null
}

export interface LedgerPage {
  generatedAtUtc: string
  rows: LedgerRow[]
  /** **How many there ACTUALLY are**, not how many came back. */
  total: number
  items: LedgerItem[]
  places: LedgerPlace[]
}

export const fetchLedger = (
  propertyId: string,
  filter: { itemId?: string | null; locationId?: string | null; take?: number; skip?: number } = {},
  options: { signal?: AbortSignal } = {},
) => {
  const query = new URLSearchParams()
  if (filter.itemId) query.set('itemId', filter.itemId)
  if (filter.locationId) query.set('locationId', filter.locationId)
  if (filter.take !== undefined) query.set('take', String(filter.take))
  if (filter.skip !== undefined) query.set('skip', String(filter.skip))
  const suffix = query.toString()

  return request<LedgerPage>(
    `/properties/${propertyId}/stock/ledger${suffix ? `?${suffix}` : ''}`,
    options.signal ? { signal: options.signal } : {})
}

export const fetchStockPars = (
  propertyId: string, options: { signal?: AbortSignal } = {},
) => request<StockParRow[]>(
  `/properties/${propertyId}/stock-pars`,
  options.signal ? { signal: options.signal } : {})

/** **Null clears the par.** Zero is a real par meaning "this place should hold none". */
export const setStockPar = (
  propertyId: string,
  body: { stockLocationId: string; stockItemId: string; parLevel: number | null },
) => request<void>(`/properties/${propertyId}/stock-pars`, { method: 'PUT', body })

/* ── physical counts: W-105 and W-106 ──────────────────────────────────────────
   START → SAVE → SUBMIT → APPROVE. Four calls where Sprint 4 had two, and the extra
   step is the control: the person who counts a shortfall is refused the approval that
   writes it off. */

export type StockCountState = 'OPEN' | 'SUBMITTED' | 'APPROVED' | 'ABANDONED'

export type CountVarianceReason =
  | 'MATCHED'
  | 'EARLIER_RECEIPT_NOT_RECORDED'
  | 'POSSIBLY_AT_LAUNDRY'
  | 'POSSIBLY_IN_CIRCULATION'
  | 'ISSUED_NOT_RECORDED'
  | 'LOSS_IN_CIRCULATION'

export type CountAdjustment = 'NONE' | 'ADJUST_UP' | 'WRITE_OFF' | 'RECOUNT'

/** One row of the count sheet.
 *
 *  NOTE WHAT IS NOT HERE: the expected quantity. That is not the screen choosing to
 *  leave a column out — the server does not send it. Pack 3: showing it "turns a count
 *  into a confirmation; people see 412, count roughly 412, and move on." A screen that
 *  receives the figure and hides it is one devtools window away from being useless. */
export interface CountSheetLine {
  stockItemId: string
  itemCode: string
  itemName: string
  itemNameAr: string | null
  kind: StockItemKind
  unit: string
  /** NULL MEANS NOT COUNTED, and it is not a zero. A person who means zero types 0. */
  countedQuantity: number | null
  countedByName: string | null
  countedByNameAr: string | null
  countedAtUtc: string | null
}

export interface CountSheetView {
  stockCountId: string
  reference: string
  state: StockCountState
  round: number
  stockLocationId: string
  locationName: string
  locationNameAr: string | null
  locationKind: StockLocationKind
  startedAtUtc: string
  startedByName: string
  startedByNameAr: string | null
  itemsOnSheet: number
  itemsCounted: number
  canSubmit: boolean
  lines: CountSheetLine[]
}

export interface CountVarianceRow {
  stockItemId: string
  itemCode: string
  itemName: string
  itemNameAr: string | null
  kind: StockItemKind
  unit: string
  expected: number
  counted: number | null
  variance: number | null
  suggestedReason: CountVarianceReason | null
  chosenAdjustment: CountAdjustment | null
  /** The two numbers the suggestion was derived from. Sent so that advice can be
   *  checked rather than believed. */
  atLaundry: number
  inCirculation: number
}

export interface CountVarianceBoard {
  stockCountId: string
  reference: string
  state: StockCountState
  round: number
  stockLocationId: string
  locationName: string
  locationNameAr: string | null
  submittedAtUtc: string | null
  submittedByName: string | null
  submittedByNameAr: string | null
  completedAtUtc: string | null
  completedByName: string | null
  completedByNameAr: string | null
  closureReason: string | null
  returnReason: string | null
  itemsOnSheet: number
  itemsCounted: number
  /** Its own figure, never folded into "matched exactly". */
  itemsNotCounted: number
  matchedExactly: number
  short: number
  over: number
  netPieces: number
  percentOfHolding: number
  /* **NO VALUE AND NO CURRENCY** — CQ-129, closed 20 September. What a count reports is
     pieces; the money figure and the currency that labelled it went in Sprint 84. */
  canApprove: boolean
  /** Why not, when it is not. "No" and "no, because you counted it" are different
   *  screens, so both halves of the answer are sent. */
  approverCounted: boolean
  rows: CountVarianceRow[]
}

export interface CountRow {
  stockCountId: string
  reference: string
  state: StockCountState
  round: number
  stockLocationId: string
  locationName: string
  locationNameAr: string | null
  startedAtUtc: string
  submittedAtUtc: string | null
  completedAtUtc: string | null
  itemsOnSheet: number
  itemsCounted: number
}

export function fetchStockLevels(
  propertyId: string, options: { locationId?: string; signal?: AbortSignal } = {},
): Promise<StockLevelView[]> {
  const query = options.locationId ? `?locationId=${options.locationId}` : ''
  return request<StockLevelView[]>(
    `/properties/${propertyId}/stock/levels${query}`,
    options.signal ? { signal: options.signal } : {})
}

export function fetchStockOnHand(
  propertyId: string, options: { signal?: AbortSignal } = {},
): Promise<StockOnHandView[]> {
  return request<StockOnHandView[]>(
    `/properties/${propertyId}/stock/on-hand`,
    options.signal ? { signal: options.signal } : {})
}

export function recordStockMovement(
  propertyId: string,
  body: {
    stockItemId: string
    kind: StockMovementKind
    quantity: number
    fromLocationId?: string
    toLocationId?: string
    reason?: string
    roomId?: string
    capturedAtUtc?: string
  },
): Promise<{ stockMovementId: string }> {
  return request(`/properties/${propertyId}/stock/movements`, { method: 'POST', body })
}

export function startStockCount(
  propertyId: string, body: { stockLocationId: string },
): Promise<{ stockCountId: string }> {
  return request(`/properties/${propertyId}/stock/counts`, { method: 'POST', body })
}

export function fetchStockCounts(
  propertyId: string, options: { signal?: AbortSignal } = {},
): Promise<CountRow[]> {
  return request<CountRow[]>(
    `/properties/${propertyId}/stock/counts`,
    options.signal ? { signal: options.signal } : {})
}

export function fetchCountSheet(
  countId: string, options: { signal?: AbortSignal } = {},
): Promise<CountSheetView> {
  return request<CountSheetView>(
    `/stock/counts/${countId}/sheet`, options.signal ? { signal: options.signal } : {})
}

/** Save and pause. Partial by design — a count sheet is walked, not filled in. */
export function saveCountLines(
  countId: string, lines: { stockItemId: string; countedQuantity: number }[],
): Promise<CountSheetView> {
  return request(`/stock/counts/${countId}/lines`, { method: 'POST', body: { lines } })
}

/** Hand it over. Computes the variance and writes NOTHING to the ledger. */
export function submitStockCount(
  countId: string, body: { notes?: string } = {},
): Promise<CountVarianceBoard> {
  return request(`/stock/counts/${countId}/submit`, { method: 'POST', body })
}

export function fetchCountVariance(
  countId: string, options: { signal?: AbortSignal } = {},
): Promise<CountVarianceBoard> {
  return request<CountVarianceBoard>(
    `/stock/counts/${countId}/variance`, options.signal ? { signal: options.signal } : {})
}

/** The only call in the count that writes to the ledger. */
export function approveStockCount(
  countId: string,
  adjustments: { stockItemId: string; adjustment: CountAdjustment }[],
): Promise<CountVarianceBoard> {
  return request(`/stock/counts/${countId}/approve`, { method: 'POST', body: { adjustments } })
}

export function returnCountForRecount(
  countId: string, reason: string,
): Promise<CountSheetView> {
  return request(`/stock/counts/${countId}/recount`, { method: 'POST', body: { reason } })
}

/** Give up on it and RELEASE THE STORE. The hold has teeth; this is the way out. */
export function abandonStockCount(
  countId: string, reason: string,
): Promise<CountVarianceBoard> {
  return request(`/stock/counts/${countId}/abandon`, { method: 'POST', body: { reason } })
}
